import { Card, CardContent, Tooltip, Typography, TypographyProps } from "@mui/material";

export default function MetricCard({
  id,
  title,
  data,
  prevData,
  percentChange,
  prevStartDate,
  prevEndDate,
  isLoadingPrevReport,
  reverse,
}: {
  id: string;
  title: string;
  data: number | string;
  prevData?: number;
  percentChange: number;
  prevStartDate: Date;
  prevEndDate: Date;
  isLoadingPrevReport: boolean;
  reverse?: boolean;
}) {
  return (
    <Card data-test={id}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: "normal" }}>
          {title}
        </Typography>
        <Typography variant="h4" component="div">
          {data}{" "}
          <DisplayChangeDetails
            previousNumber={prevData}
            percentChange={percentChange}
            prevStartDate={prevStartDate}
            prevEndDate={prevEndDate}
            isLoading={isLoadingPrevReport}
            reverse={reverse}
          />
        </Typography>
      </CardContent>
    </Card>
  );
}

interface DisplayChangeDetailsProps {
  percentChange: number;
  previousNumber?: number;
  reverse?: boolean;
  isLoading?: boolean;
  prevStartDate?: Date;
  prevEndDate?: Date;
}

/**
 * Display a percent change with a + for positive and - for negative, and a color based on the value
 * @param percentChange The percent change to display as a whole number
 * @param reverse If true, the color will be reversed (green for negative, red for positive)
 * @param isLoading If true, the color will be disabled
 * @param previousNumber The previous number to display in the tooltip
 * @param prevStartDate The previous report start date to display in the tooltip
 * @param prevEndDate The previous report end date to display in the tooltip
 * @param rest Typography props
 * @returns
 */
export function DisplayChangeDetails(props: TypographyProps & DisplayChangeDetailsProps) {
  const { percentChange, reverse, isLoading, previousNumber, prevStartDate, prevEndDate, ...rest } = props;
  const percentChangeDisplay = percentChange > 0 ? `+${percentChange}` : percentChange;

  let color;
  if (isLoading) {
    color = "text.disabled";
  } else if (percentChange === 0) {
    color = "text.primary";
  } else if (reverse ? percentChange > 0 : percentChange < 0) {
    color = "error.main";
  } else if (reverse ? percentChange < 0 : percentChange > 0) {
    color = "success.main";
  }

  return (
    <Tooltip
      arrow
      title={
        <>
          <Typography>
            Previous Count:
            <br />
            {previousNumber ?? 0}
          </Typography>
          {prevStartDate && prevEndDate ? (
            <Typography sx={{ mt: 1 }}>
              Previous Date Range:
              <br />
              {prevStartDate.toLocaleDateString()} - {prevEndDate.toLocaleDateString()}
            </Typography>
          ) : null}
        </>
      }
    >
      <Typography {...rest} component="span" variant="subtitle1" sx={{ fontWeight: "normal" }} color={color}>
        {percentChangeDisplay}%
      </Typography>
    </Tooltip>
  );
}
